import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, LayoutDashboard, Building2, Users, BarChart3, Home, UserCog, Ticket, ClipboardList, Menu, X } from "lucide-react";
import NotificationsButton from "@/components/NotificationsButton";
import InstallButton from "@/components/InstallButton";

const NAV_BY_ROLE = {
  super_admin: [
    { to: "/a/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/a/estates", label: "Estates", icon: Building2, testid: "nav-estates" },
    { to: "/a/managers", label: "Managers", icon: UserCog, testid: "nav-managers" },
    { to: "/a/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  ],
  estate_manager: [
    { to: "/m/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/m/units", label: "Units", icon: Home, testid: "nav-units" },
    { to: "/m/residents", label: "Residents", icon: Users, testid: "nav-residents" },
    { to: "/m/tickets", label: "Tickets", icon: ClipboardList, testid: "nav-tickets" },
  ],
  resident: [
    { to: "/r/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/r/tickets", label: "My Tickets", icon: Ticket, testid: "nav-tickets" },
  ],
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = NAV_BY_ROLE[user?.role] || [];

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // Lock body scroll when drawer open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [drawerOpen]);

  const roleLabel =
    user?.role === "super_admin" ? "Super Admin" :
    user?.role === "estate_manager" ? "Estate Manager" : "Resident";

  const SidebarContent = ({ inDrawer = false }) => (
    <>
      <div className="px-5 py-4 lg:px-6 lg:py-5 border-b border-zinc-200 flex items-center gap-3">
        <img src="/icons/icon-192.png" alt="HHL logo" className="h-9 w-9 border border-zinc-200" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl font-black tracking-tighter leading-none">HHL</div>
          <div className="label-eyebrow mt-0.5">Estate Services</div>
        </div>
        {inDrawer && (
          <button
            onClick={() => setDrawerOpen(false)}
            data-testid="drawer-close-btn"
            className="p-2 -mr-2 text-zinc-600 hover:text-black lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {nav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm font-medium border-l-2 transition-colors ${
                  isActive
                    ? "border-black bg-zinc-50 text-black"
                    : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-black"
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-zinc-200 p-4 space-y-2">
        <div>
          <div className="text-sm font-semibold text-black truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
          <div className="label-eyebrow mt-1" data-testid="sidebar-user-role">{roleLabel}</div>
        </div>
        <InstallButton />
        <div className="flex items-center gap-2">
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="flex-1 flex items-center justify-center gap-2 border border-zinc-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-black hover:text-white hover:border-black transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign Out
          </button>
          <NotificationsButton />
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col lg:flex-row">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-zinc-200 flex items-center justify-between px-4 h-14" data-testid="mobile-topbar">
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="drawer-open-btn"
          className="p-2 -ml-2 text-zinc-800 hover:text-black"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" strokeWidth={1.75} />
        </button>
        <div className="flex items-center gap-2">
          <img src="/icons/icon-192.png" alt="HHL" className="h-7 w-7 border border-zinc-200" />
          <div>
            <div className="font-display text-base font-black tracking-tighter leading-none">HHL</div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 leading-none mt-0.5">Estate Services</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <InstallButton compact />
          <NotificationsButton />
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-64 border-r border-zinc-200 bg-white flex-col shrink-0 sticky top-0 h-screen"
        data-testid="app-sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            data-testid="drawer-backdrop"
          />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white border-r border-zinc-200 flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
            <SidebarContent inDrawer />
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 overflow-x-hidden min-w-0">
        {children}
      </main>
    </div>
  );
}
